import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import { marked } from "marked";
import { ApiClientError } from "@victor/api-client";
import type {
  DocumentLegalCode,
  SuperAdminLegalDocSummary,
} from "@victor/types";
import { api } from "../../lib/api";
import { Button } from "../../ui/Button";
import { Textarea } from "../../ui/Textarea";
import { Input } from "../../ui/Input";
import { Modal } from "../../ui/Modal";
import { PageSpinner } from "../../ui/Spinner";

/**
 * Ouvre un PDF servi par une route authentifiée. Le `<a href>` ne porte
 * pas l'Authorization header → on fait un fetch manuel avec le token,
 * crée un blob URL, et l'ouvre dans un nouvel onglet. Le blob est libéré
 * après quelques minutes.
 */
async function openAuthenticatedPdf(url: string): Promise<void> {
  const token = window.__victorAdminToken ?? null;
  if (!token) {
    alert("Session expirée. Veuillez vous reconnecter.");
    return;
  }
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    alert(`Échec téléchargement PDF (${res.status})`);
    return;
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
}

const DOC_LIBELLES: Record<DocumentLegalCode, string> = {
  cgv: "Conditions Générales de Vente",
  cgu: "Conditions Générales d'Utilisation",
  dpa: "Data Processing Agreement (DPA)",
  sla: "Service Level Agreement (SLA)",
  confidentialite: "Politique de confidentialité",
};

export function LegalPage() {
  const [opened, setOpened] = useState<DocumentLegalCode | null>(null);

  const listQ = useQuery({
    queryKey: ["legal-list"],
    queryFn: () => api.legal.list(),
  });

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold tracking-tight text-navy-800 sm:text-3xl">
          Documents juridiques
        </h1>
        <p className="mt-1.5 text-sm text-navy-600">
          CGV, CGU, DPA, SLA et Politique de confidentialité applicables aux
          agences MyVictor · consultation, édition Markdown versionnée et PDF.
        </p>
      </motion.header>

      {listQ.isLoading ? <PageSpinner /> : null}

      {listQ.data ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {listQ.data.map((d) => (
            <DocCard
              key={d.documentCode}
              doc={d}
              onOpen={() => setOpened(d.documentCode)}
            />
          ))}
        </div>
      ) : null}

      {opened ? (
        <EditorModal code={opened} onClose={() => setOpened(null)} />
      ) : null}
    </div>
  );
}

function DocCard({
  doc,
  onOpen,
}: {
  doc: SuperAdminLegalDocSummary;
  onOpen: () => void;
}) {
  const published = !!doc.publiAt;
  return (
    <div className="flex flex-col rounded-2xl bg-white p-5 ring-1 ring-navy-100 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy-50 text-[11px] font-bold uppercase tracking-wide text-navy-600">
            {doc.documentCode}
          </span>
          <h3 className="text-base font-bold leading-tight text-navy-800">
            {doc.libelle}
          </h3>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${
            published
              ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
              : "bg-navy-100 text-navy-500 ring-navy-200"
          }`}
        >
          {doc.versionCourante ? `v${doc.versionCourante}` : "brouillon"}
        </span>
      </div>

      <p className="mt-3 text-sm text-navy-500">
        {published
          ? `Publié le ${new Date(doc.publiAt!).toLocaleDateString("fr-FR")}`
          : "Aucune version publiée"}
        {" · "}
        {doc.nbVersions} version{doc.nbVersions > 1 ? "s" : ""}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-navy-100 pt-4">
        <Button onClick={onOpen}>Consulter / Modifier</Button>
        {doc.versionCourante ? (
          <button
            type="button"
            onClick={() =>
              void openAuthenticatedPdf(
                api.legal.pdfUrl(doc.documentCode, doc.versionCourante!),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-xl border border-navy-200 px-3 py-2 text-sm font-semibold text-navy-700 transition-colors hover:bg-navy-50"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" />
            </svg>
            PDF
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ===========================================================================
// Editor modal · édition Markdown + preview + publication
// ===========================================================================

function EditorModal({
  code,
  onClose,
}: {
  code: DocumentLegalCode;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"edit" | "preview" | "versions">("edit");
  const [contenu, setContenu] = useState<string>("");
  const [version, setVersion] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const detailQ = useQuery({
    queryKey: ["legal-current", code],
    queryFn: () => api.legal.getCurrent(code),
    retry: false,
  });

  const versionsQ = useQuery({
    queryKey: ["legal-versions", code],
    queryFn: () => api.legal.listVersions(code),
    enabled: tab === "versions",
  });

  useEffect(() => {
    if (detailQ.data) {
      setContenu(detailQ.data.contenuMarkdown);
      setVersion(detailQ.data.version);
      setDirty(false);
    }
  }, [detailQ.data]);

  const publishMut = useMutation({
    mutationFn: () =>
      api.legal.publish(code, {
        contenuMarkdown: contenu,
        version: version || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["legal-list"] });
      void queryClient.invalidateQueries({ queryKey: ["legal-current", code] });
      void queryClient.invalidateQueries({ queryKey: ["legal-versions", code] });
      setConfirmOpen(false);
      setDirty(false);
    },
  });

  const previewHtml = useMemo(() => {
    try {
      return marked.parse(contenu) as string;
    } catch {
      return "<p>Erreur de parsing</p>";
    }
  }, [contenu]);

  const sha = useSha256(contenu);

  return (
    <Modal
      open
      onClose={onClose}
      title={DOC_LIBELLES[code]}
      size="lg"
    >
      <div className="space-y-4">
        {/* Tabs internes */}
        <div className="flex gap-1 rounded-xl bg-navy-50/60 p-1">
          {(["edit", "preview", "versions"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-bold transition-colors ${
                tab === t
                  ? "bg-white text-navy-800 shadow-sm"
                  : "text-navy-500 hover:text-navy-700"
              }`}
            >
              {t === "edit"
                ? "Éditer"
                : t === "preview"
                  ? "Aperçu"
                  : "Historique"}
            </button>
          ))}
        </div>

        {detailQ.isLoading ? <PageSpinner /> : null}

        {tab === "edit" && detailQ.data ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Version à publier"
                value={version}
                onChange={(e) => {
                  setVersion(e.target.value);
                  setDirty(true);
                }}
                placeholder="ex. 1.1, 2.0…"
              />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-navy-500 mb-1">
                  Empreinte SHA-256 (calcul local)
                </p>
                <code className="block truncate rounded-xl border border-navy-100 bg-white px-3 py-2 text-xs font-mono text-navy-700">
                  {sha}
                </code>
              </div>
            </div>
            <Textarea
              label="Contenu Markdown"
              value={contenu}
              onChange={(e) => {
                setContenu(e.target.value);
                setDirty(true);
              }}
              rows={20}
            />
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-navy-100">
              <p className="text-xs text-navy-500">
                {dirty
                  ? "Modifications non publiées."
                  : "Aucune modification depuis la dernière publication."}
              </p>
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={!dirty || publishMut.isPending}
              >
                {publishMut.isPending
                  ? "Publication…"
                  : "Publier nouvelle version"}
              </Button>
            </div>
            {publishMut.error ? (
              <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
                {publishMut.error instanceof ApiClientError
                  ? publishMut.error.message
                  : "Erreur de publication."}
              </p>
            ) : null}
          </div>
        ) : null}

        {tab === "preview" ? (
          <div
            className="prose prose-sm max-w-none rounded-xl bg-white p-4 ring-1 ring-navy-100 max-h-[600px] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        ) : null}

        {tab === "versions" && versionsQ.data ? (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {versionsQ.data.map((v) => (
              <div
                key={v.version}
                className="flex items-center justify-between rounded-xl border border-navy-100 bg-white px-4 py-3"
              >
                <div>
                  <p className="font-bold text-navy-800">v{v.version}</p>
                  <p className="text-xs text-navy-500">
                    Publié le{" "}
                    {new Date(v.publiAt).toLocaleDateString("fr-FR")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    void openAuthenticatedPdf(api.legal.pdfUrl(code, v.version))
                  }
                  className="rounded-lg border border-navy-200 px-3 py-1.5 text-xs font-bold text-navy-700 hover:bg-navy-50"
                >
                  PDF
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {confirmOpen ? (
        <Modal
          open
          onClose={() => setConfirmOpen(false)}
          title="Publier une nouvelle version"
          size="sm"
        >
          <div className="space-y-3">
            <p className="text-sm text-navy-700">
              Vous êtes sur le point de publier une nouvelle version du
              document <strong>{DOC_LIBELLES[code]}</strong>.
            </p>
            <p className="text-sm text-navy-600">
              Cette opération est tracée dans l'audit chain-hash et le PDF
              est régénéré automatiquement.
            </p>
            <ul className="text-xs text-navy-500 space-y-1">
              <li>
                Version cible :{" "}
                <strong>
                  {version || "auto-incrément"}
                </strong>
              </li>
              <li>
                SHA-256 : <code className="font-mono">{sha.slice(0, 16)}…</code>
              </li>
            </ul>
            <div className="flex justify-end gap-2 pt-2 border-t border-navy-100">
              <Button
                variant="ghost"
                onClick={() => setConfirmOpen(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={() => publishMut.mutate()}
                disabled={publishMut.isPending}
              >
                {publishMut.isPending ? "Publication…" : "Confirmer"}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// SHA-256 via SubtleCrypto (preview client-side)
// ---------------------------------------------------------------------------

function useSha256(input: string): string {
  const [sha, setSha] = useState("…");
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const buf = new TextEncoder().encode(input);
        const hash = await crypto.subtle.digest("SHA-256", buf);
        const hex = Array.from(new Uint8Array(hash))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        if (!aborted) setSha(hex);
      } catch {
        if (!aborted) setSha("-");
      }
    })();
    return () => {
      aborted = true;
    };
  }, [input]);
  return sha;
}
